<template>
  <section>
    <div ref="cw" class="d-block">
      <canvas
        v-show="renderAs === ''"
        ref="c"
        :width="canvasWidth"
        :height="canvasHeight"
        :style="canvasStyles"
        aria-labelledby="canvas-description"
        role="img"
      >
        <p id="canvas-description" class="sr-only">{{ $t('interactivePersonalizationCanvas') }}</p>
      </canvas>
    </div>
  </section>
</template>

<script>
import { fabric } from 'fabric';
import DOMPurify from 'dompurify';
import FontFaceObserver from 'fontfaceobserver';
import { buildHomeSafeCanvasStyles } from '../mixins/homeSafeCanvasStyles';

export default {
  name: 'CustomDesign',
  props: {
    title: {
      type: String,
      default: '',
      required: false,
    },
    width: {
      type: [Number, String],
      default: null,
      required: false,
    },
    height: {
      type: [Number, String],
      default: null,
      required: false,
    },
    baseTagSvgImgPath: {
      type: [String, Boolean],
      default: null,
      required: true,
    },
    designSvgImgPath: {
      type: [String, Boolean],
      default: null,
      required: false,
    },
    canvasBGColor: {
      type: String,
      default: 'transparent',
      required: false,
    },
    boundaryPadding: {
      type: [String, Number],
      default: 0,
      required: false,
    },
    petNameText: {
      type: Object,
      default: null,
      required: false,
    },
    phoneText: {
      type: [Object, Boolean],
      default: null,
      required: false,
    },
    homeSafeIdText: {
      type: [Object, Boolean],
      default: null,
      required: false,
    },
    leftZoneLine1Text: {
      type: [Object, Boolean],
      default: null,
      required: false,
    },
    leftZoneLine2Text: {
      type: [Object, Boolean],
      default: null,
      required: false,
    },
    hotlineText: {
      type: [Object, Boolean],
      default: null,
      required: false,
    },
    urlText: {
      type: [Object, Boolean],
      default: null,
      required: false,
    },
    showRedesignedTagBack: {
      type: Boolean,
      default: false,
      required: false,
    },
    showLabels: {
      type: Boolean,
      default: true,
      required: false,
    },
    selectable: {
      type: Boolean,
      default: false,
      required: false,
    },
    scale: {
      type: [String, Number],
      default: 0.6,
      required: false,
    },
    exportable: {
      type: Boolean,
      default: false,
      required: false,
    },
    value: {
      type: Object,
      default: null,
      required: false,
    },
    preview: {
      type: Boolean,
      default: false,
      required: false,
    },
    renderAs: {
      type: String,
      default: '',
      required: false,
    },
    canvasStyles: {
      type: String,
      default: '',
      required: false,
    },
  },
  data() {
    return {
      canvas: new fabric.StaticCanvas(),
      exportCanvas: null,
      isComponentDestroyed: false,
      canvasWidth: 0,
      canvasHeight: 0,
      baseTagRingRadius: 38,
      baseTagRadius: 208.6044724409,
      baseTagSvgImg: null,
      designSvgImg: null,
      qrCodePlaceholderSvgImg: null,
      windowWidth: window.innerWidth,
      debugMode: false,
      designerMode: !!this.value?.designerMode || false,
    };
  },
  computed: {
    computedCanvas() {
      return JSON.stringify({
        baseTagSvgImgPath: this.baseTagSvgImgPath,
        designSvgImgPath: this.designSvgImgPath,
      });
    },
    computedText() {
      return JSON.stringify({
        petNameText: this.petNameText,
        phoneText: this.phoneText,
        homeSafeIdText: this.homeSafeIdText,
        leftZoneLine1Text: this.leftZoneLine1Text,
        leftZoneLine2Text: this.leftZoneLine2Text,
        hotlineText: this.hotlineText,
        urlText: this.urlText,
        showLabels: this.showLabels,
        debugMode: this.debugMode,
        showRedesignedTagBack: this.showRedesignedTagBack,
        scale: this.scale,
        boundaryPadding: this.boundaryPadding,
        height: this.height,
        width: this.width,
      });
    },
    designerModeOverrideConfig: {
      get() {
        return this.value;
      },
      set(config) {
        const safeConfig = this.sanitizeDesignConfig(config);
        this.$emit('input', safeConfig);
      },
    },
  },
  watch: {
    computedCanvas() {
      this.$nextTick(() => {
        this.loadCanvas();
      });
    },
    computedText() {
      this.$nextTick(() => {
        this.redrawCanvas();
      });
    },
  },
  created() {
    window.addEventListener('resize', this.onWindowResize);
  },
  mounted() {
    this.loadCanvas();
  },
  destroyed() {
    // Flag first so any in-flight async SVG-load callbacks (drawOnCanvas/tryProcess) bail out
    // instead of rendering into canvases we're about to dispose.
    this.isComponentDestroyed = true;
    window.removeEventListener('resize', this.onWindowResize);
    this.canvas?.dispose();
    this.exportCanvas?.dispose();
  },
  methods: {
    buildTagBackTextGroup(fabric) {
      const TG = new fabric.Group([]);
      const entries = [
        this.petNameText,
        this.phoneText,
        this.homeSafeIdText,
        this.leftZoneLine1Text,
        this.leftZoneLine2Text,
        this.hotlineText,
        this.urlText,
      ];
      for (const cfg of entries) {
        if (cfg && cfg.text) TG.addTextLine(cfg.text, cfg);
      }
      return TG;
    },

    // Wordmark styles bake in absolute font sizes, which is safe only because these labels
    // never pass through addTextLine()/autoScaleFonts() — their fontSize is fixed here.
    buildTagBackLabelGroup(fabric) {
      const DARK_BLUE = '#003c71';
      const LABEL_X = 295;
      const items = [
        new fabric.Line([110, -117, 275, -180], { stroke: DARK_BLUE, strokeWidth: 5, objectCaching: false }),
        new fabric.Text(this.$t('personalizePreview.labelOptionsText'), {
          left: LABEL_X, top: -183, originX: 'left', originY: 'center',
          fill: DARK_BLUE, fontSize: 36, fontWeight: 'bold', fontFamily: 'Lato', objectCaching: false,
        }),
      ];
      if (this.phoneText) {
        items.push(
          new fabric.Line([157,  -88, 275,  -88], { stroke: DARK_BLUE, strokeWidth: 5, objectCaching: false }),
          new fabric.Text(this.$t('personalizePreview.labelPhone'), {
            left: LABEL_X, top: -63, originX: 'left', originY: 'center',
            fill: DARK_BLUE, fontSize: 36, fontWeight: 'bold', fontFamily: 'Lato', objectCaching: false,
          }),
        );
      }
      const profileLinkLabel = this.$t('personalizePreview.labelProfileLink');
      items.push(
        new fabric.Line([167,  27, 275,   27], { stroke: DARK_BLUE, strokeWidth: 5, objectCaching: false }),
        new fabric.Text(profileLinkLabel, {
          left: LABEL_X, top: 60, originX: 'left', originY: 'center',
          fill: DARK_BLUE, fontSize: 24, fontFamily: 'Lato', objectCaching: false,
          styles: buildHomeSafeCanvasStyles(profileLinkLabel, { fill: DARK_BLUE, fontSize: 24 }),
        }),
      );
      // Only label the hotline leader line/text if a hotline number is actually drawn on the tag.
      if (this.hotlineText && this.hotlineText.text) {
        const hotlineLabel = this.$t('personalizePreview.labelHotline');
        items.push(
          new fabric.Line([130,  185, 275,  185], { stroke: DARK_BLUE, strokeWidth: 5, objectCaching: false }),
          new fabric.Text(hotlineLabel, {
            left: LABEL_X, top: 190, originX: 'left', originY: 'center',
            fill: DARK_BLUE, fontSize: 24, fontFamily: 'Lato', objectCaching: false,
            styles: buildHomeSafeCanvasStyles(hotlineLabel, { fill: DARK_BLUE, fontSize: 24 }),
          }),
        );
      }
      return new fabric.Group(items, { objectCaching: false });
    },

    buildDebugTagCircle(fabric) {
      return new fabric.Circle({
        radius: fabric.BASE_TAG_RADIUS,
        top: fabric.BASE_TAG_RING_RADIUS,
        originY: 'center',
        originX: 'center',
        stroke: 'red',
        fill: 'transparent',
      });
    },

    buildSafetyTagCircle(fabric) {
      return new fabric.Circle({
        radius: 0.92 * fabric.BASE_TAG_RADIUS,
        top: fabric.BASE_TAG_RING_RADIUS,
        originY: 'center',
        originX: 'center',
        stroke: 'red',
        strokeDashArray: [10, 10],
        fill: 'transparent',
        excludeFromExport: true,
      });
    },

    loadCanvas() {
      // Set SVG Images
      this.baseTagSvgImg = this.baseTagSvgImgPath || require('@/assets/images/dyot/SilverTagBase.svg');
      this.designSvgImg = this.designSvgImgPath ? this.designSvgImgPath : null;
      this.qrCodePlaceholderSvgImg = require('@/assets/images/dyot/QRCodePlaceholder.svg');

      // Add Prototype Methods
      this.addCustomPrototypeMethods();

      if (this.canvas) {
        this.canvas.dispose();
        this.canvas = null;
      }

      // Init
      this.canvas = new fabric.StaticCanvas(this.$refs.c);

      // Set Background Color
      this.canvas.backgroundColor = this.canvasBGColor;

      // Set dimensions from parent (cw: canvas-wrapper)
      this.canvasWidth = Number(this.width) || this.$refs.cw.clientWidth;
      this.canvasHeight = Number(this.height) || this.$refs.cw.clientHeight || this.canvasWidth;
      const canvasInstance = this.canvas;
      this.$nextTick(() => {
        if (!canvasInstance || canvasInstance !== this.canvas || !this.canvas.lowerCanvasEl) return;
        this.canvas.setHeight(this.canvasHeight);
        this.canvas.setWidth(this.canvasWidth);

        // Add Title to canvas
        if (this.title) this.addCanvasTitle(this.title);

        // Draw on Canvas
        this.drawOnCanvas(this.baseTagSvgImg, this.designSvgImg);

        // Render Canvas
        this.renderCanvas();

        if (this.exportable) this.loadExportCanvas();
      });
    },
    redrawCanvas() {
      if (!this.canvas) {
        this.loadCanvas();
        return;
      }
      const width = Number(this.width) || this.$refs.cw.clientWidth;
      const height = Number(this.height) || this.$refs.cw.clientHeight || width;
      this.canvasWidth = width;
      this.canvasHeight = height;
      this.canvas.setWidth(width);
      this.canvas.setHeight(height);
      this.baseTagSvgImg = this.baseTagSvgImgPath || require('@/assets/images/dyot/SilverTagBase.svg');
      this.designSvgImg = this.designSvgImgPath ? this.designSvgImgPath : null;
      this.qrCodePlaceholderSvgImg = require('@/assets/images/dyot/QRCodePlaceholder.svg');
      // Dispose the previous export canvas before dropping the reference, otherwise repeated
      // redraws (e.g. every computedText change) leak Fabric canvas instances.
      this.exportCanvas?.dispose();
      this.exportCanvas = null;
      this.drawOnCanvas(this.baseTagSvgImg, this.designSvgImg);

      if (this.exportable) this.loadExportCanvas();
    },
    loadExportCanvas() {
      // Guard against leaking a previously created export canvas (e.g. loadCanvas() re-running
      // on computedCanvas changes) since this method can be invoked repeatedly on the same instance.
      this.exportCanvas?.dispose();
      this.exportCanvas = new fabric.StaticCanvas().setHeight(415).setWidth(415);
      this.drawOnCanvas(this.baseTagSvgImg, this.designSvgImg, true);
      this.renderCanvas(true);
      return this.exportCanvas;
    },
    drawOnCanvas(baseSVG, designSVG = null, forExport = false) {
      let bResult = null;
      let dResult = null;
      let qResult = null;
      let dReady = !designSVG;
      let qReady = !this.showRedesignedTagBack || !this.qrCodePlaceholderSvgImg;

      const processCanvas = (bObjs, bOpts, dObjs, dOpts, qObjs, qOpts) => {
          // Base & Design Layer Group
          const BG = new fabric.Group([new fabric.util.groupSVGElements(bObjs, bOpts)], {
              originY: 'center',
              originX: 'center',
              top: 0,
              left: 0,
            }),
            DG = new fabric.Group(dObjs ? [new fabric.util.groupSVGElements(dObjs, dOpts)] : []);
          DG.setPositionByOrigin(new fabric.Point(DG.left, DG.top + fabric.BASE_TAG_RING_RADIUS), 'center', 'center');
          // Add Text Layer
          if (!this.showRedesignedTagBack && this.petNameText) {
            fabric.DESIGNER_MODE = this.designerMode;
            const TG = new fabric.Group([]);
            if (this.petNameText && this.petNameText.text) {
              this.FabricPetNameText = TG.addTextLine(this.petNameText.text, this.petNameText);
            }
            if (this.homeSafeIdText && this.homeSafeIdText.text) {
              this.FabricHomeSafeIdText = TG.addTextLine(this.homeSafeIdText.text, this.homeSafeIdText);
            }
            if (this.phoneText && this.phoneText.text) {
              this.FabricPhoneText = TG.addTextLine(this.phoneText.text, this.phoneText);
            }
            DG.addWithUpdate(TG);
            if (this.debugMode) {
              // Draw Text Boundary
              TG.addWithUpdate(
                new fabric.Rect({
                  top: this.FabricPetNameText?.offsetY,
                  originY: 'bottom',
                  originX: 'center',
                  width: this.FabricPetNameText?.width,
                  height: this.FabricPetNameText?.height,
                  fill: 'transparent',
                  stroke: 'blue',
                })
              );
              TG.addWithUpdate(
                new fabric.Rect({
                  top: fabric.BASE_TAG_RING_RADIUS,
                  originY: 'center',
                  originX: 'center',
                  width: fabric.BASE_TAG_RADIUS * 2,
                  height: 0,
                  fill: 'black',
                  stroke: 'black',
                })
              );
              TG.addWithUpdate(
                new fabric.Rect({
                  top: this.FabricPhoneText?.offsetY,
                  originY: 'top',
                  originX: 'center',
                  width: this.FabricPhoneText?.width,
                  height: this.FabricPhoneText?.height,
                  fill: 'transparent',
                  stroke: 'blue',
                })
              );
            }
          }
          // Debug Mode
          if (this.debugMode) {
            // Draw Rectangular Boundary on Base Layer Group
            BG.addWithUpdate(
              new fabric.Rect({
                originY: 'center',
                originX: 'center',
                width: BG.width,
                height: BG.height,
                fill: 'transparent',
                stroke: 'red',
              })
            );
            // Draw Circular Boundary on Base Layer Group / Tag - Circle
            DG.addWithUpdate(this.buildDebugTagCircle(fabric));
          }
          if (!forExport && this.exportable && this.designerMode && !this.preview) {
            // Draw Circular Safety Boundary on Base Layer Group / Tag - Circle
            BG.addWithUpdate(this.buildSafetyTagCircle(fabric));
          }
          const finalizeCanvas = () => {
            if (!forExport) {
              BG.addWithUpdate(DG);
              // Scale to fit in available space
              const availableCanvasHeight =
                  (Number(this.canvasHeight) - Number(this.boundaryPadding) - (this.canvasTitleHeight || 0)) * (Number(this.scale) || 1),
                availableCanvasWidth = (Number(this.canvasWidth) - Number(this.boundaryPadding)) * (Number(this.scale) || 1);
              BG.scaleToCanvas(availableCanvasWidth, availableCanvasHeight);
              this.canvas.clear();
              this.canvas.backgroundColor = this.canvasBGColor;
              if (this.title) this.addCanvasTitle(this.title);
              this.canvas.centerObject(BG).add(BG);
            } else {
              if (!this.exportCanvas) return;
              this.exportCanvas.backgroundColor = 'transparent';
              // Add to export canvas;
              this.exportCanvas.centerObject(DG).add(DG);
            }
          };

          if (this.showRedesignedTagBack) {
            const qrSize = fabric.BASE_TAG_RADIUS;
            const cx = 0;
            const cy = fabric.BASE_TAG_RING_RADIUS;

            if (qObjs && qObjs.length) {
              const qrGroup = fabric.util.groupSVGElements(qObjs, qOpts);
              qrGroup.set({
                originX: 'center',
                originY: 'center',
                top: cy,
                left: cx,
                objectCaching: false,
              });
              qrGroup.scaleToWidth(qrSize);
              DG.addWithUpdate(qrGroup);
            }
            fabric.DESIGNER_MODE = this.designerMode;
            DG.addWithUpdate(this.buildTagBackTextGroup(fabric));
            if (this.showLabels) {
              DG.addWithUpdate(this.buildTagBackLabelGroup(fabric));
            }
            finalizeCanvas();
          } else {
            finalizeCanvas();
          }
      };

      const tryProcess = () => {
        // Guard against async SVG-load callbacks resolving after the component (and its
        // canvases) have already been destroyed/disposed.
        if (this.isComponentDestroyed) return;
        if (bResult && dReady && qReady) {
          processCanvas(bResult.objs, bResult.opts, dResult?.objs ?? null, dResult?.opts ?? null, qResult?.objs ?? null, qResult?.opts ?? null);
        }
      };

      fabric.loadSVGFromURL(baseSVG, (bObjs, bOpts) => {
        bResult = { objs: bObjs, opts: bOpts };
        tryProcess();
      });

      if (designSVG) {
        fabric.loadSVGFromURL(designSVG, (dObjs, dOpts) => {
          dResult = { objs: dObjs, opts: dOpts };
          dReady = true;
          tryProcess();
        });
      }

      if (this.showRedesignedTagBack && this.qrCodePlaceholderSvgImg) {
        fabric.loadSVGFromURL(this.qrCodePlaceholderSvgImg, (qObjs, qOpts) => {
          qResult = { objs: qObjs, opts: qOpts };
          qReady = true;
          tryProcess();
        });
      }
    },
    // Custom ProtoTypes
    addCustomPrototypeMethods() {
      fabric.Object.prototype.objectCaching = false;
      fabric.Object.prototype.statefullCache = false;
      fabric.Object.prototype.noScaleCache = true;

      // TAG CONSTANTS
      fabric.DEBUG = this.debugMode;
      fabric.DESIGNER_MODE = this.designerMode;
      fabric.BASE_TAG_RADIUS = this.baseTagRadius;
      fabric.BASE_TAG_RING_RADIUS = this.baseTagRingRadius;

      // Fabric Static Canvas
      fabric.StaticCanvas.prototype.renderAll = function () {
        const canvasToDrawOn = this?.contextContainer;
        if (!canvasToDrawOn) return;
        this.renderCanvas(canvasToDrawOn, this._objects);
        return this;
      };
      fabric.StaticCanvas.prototype.clearContext = function (ctx) {
        if (!ctx) return;
        fabric.util.clearFabricFontCache();
        ctx.clearRect(0, 0, this.width, this.height);
        return this;
      };

      // Fabric Text
      fabric.Text.prototype.setCustomFontFamily = function (fontFamily) {
        if (!fontFamily) return this;
        new FontFaceObserver(fontFamily).load().then(() => (this.fontFamily = fontFamily));
        this.fontFamily = fontFamily;
        return this;
      };
      fabric.Text.prototype.setMaxCharacters = function (maxChars, callback = null) {
        this.maxCharacters = maxChars;
        if (this.text.length >= maxChars) {
          if (callback && callback instanceof Function) callback(this.text.length, maxChars);
          else {
            this.text = this.text.substr(0, maxChars);
          }
        }
        return this;
      };
      fabric.Text.prototype.setFontSize = function (fontSize, minFontSizeCallback = null, maxFontSizeCallback = null) {
        if (!fontSize) return this;
        if (fontSize > this.maxFontSize)
          if (maxFontSizeCallback instanceof Function) maxFontSizeCallback(this);
          else this.fontSize = this.maxFontSize;
        else if (fontSize < this.minFontSize)
          if (minFontSizeCallback instanceof Function) minFontSizeCallback(this);
          else this.fontSize = this.minFontSize;
        else this.fontSize = fontSize;
        this.initDimensions();
        this.setCoords();
        this.setPosition(this.top, this.left);
        return this;
      };
      fabric.Text.prototype.setPosition = function (newTop = null, newLeft = null) {
        if ((newTop == null && newLeft == null) || (newTop === this.top && newLeft === this.left)) return this;
        if (!isNaN(newTop)) this.offsetY = this.top = Number(newTop);
        if (!isNaN(newLeft)) this.offsetX = this.left = Number(newLeft);
        this.initDimensions();
        this.setCoords();
        return this;
      };
      fabric.Text.prototype.addTextLabel = function (labelContent, labelOptions, lineOptions) {
        const LabelText = new fabric.Text(labelContent, {
          left: fabric.BASE_TAG_RADIUS * 2 + Number(labelOptions?.offsetX),
          top: Number(this.offsetY) + Number(labelOptions?.offsetY),
          originX: 'center',
          originY: 'center',
          fill: '#02294f',
          objectCaching: false,
          ...labelOptions,
        });
        const LabelLine = new fabric.Line(
          [
            fabric.BASE_TAG_RADIUS / lineOptions?.offsetX1 || 1,
            Number(this.offsetY) + lineOptions?.offsetY1 || 0,
            LabelText.left - LabelText.width / 2 - lineOptions?.offsetX2 || 0,
            LabelText.top + lineOptions?.offsetY2 || 0,
          ],
          {
            strokeWidth: 5,
            stroke: '#02294f',
            originX: 'center',
            originY: 'center',
            objectCaching: false,
            ...lineOptions,
          }
        );
        this.group.addWithUpdate(new fabric.Group([LabelText, LabelLine]), {
          originX: 'center',
          originY: 'center',
          objectCaching: false,
        });
        return this;
      };
      fabric.Text.prototype.autoScaleFonts = function (boundaryPadding = 0) {
        const maxWidthOfTextChord = (debugMode = false) => {
            // Perpendicular Leg
            const distFromCenterY = Math.abs(
                this.getCenterPoint().distanceFrom(new fabric.Point(0, fabric.BASE_TAG_RING_RADIUS))
              ),
              // Hypotenuse
              h = fabric.BASE_TAG_RADIUS,
              // Calc Chord Leg (* 2 chord length / max width)
              mw = Math.sqrt(Math.pow(h, 2) - Math.pow(Math.min(distFromCenterY, h), 2)) * 2 - boundaryPadding * 2;
            if (fabric.DEBUG && debugMode) {
              this.group?.addWithUpdate(
                new fabric.Rect({
                  top: this.getCenterPoint().y - 0.47 * this.fontSize,
                  left: this.getCenterPoint().x,
                  originY: 'center',
                  originX: 'center',
                  width: mw,
                  height: 1,
                  fill: 'transparent',
                  stroke: 'red',
                })
              );
              this.group?.addWithUpdate(
                new fabric.Rect({
                  top: this.getCenterPoint().y,
                  left: this.getCenterPoint().x,
                  originY: 'center',
                  originX: 'center',
                  width: mw,
                  fill: 'transparent',
                  stroke: 'yellow',
                })
              );
              this.group?.addWithUpdate(
                new fabric.Rect({
                  top: this.getCenterPoint().y + 0.37 * this.fontSize,
                  left: this.getCenterPoint().x,
                  originY: 'center',
                  originX: 'center',
                  width: mw,
                  height: 1,
                  fill: 'transparent',
                  stroke: 'red',
                })
              );
            }
            return mw;
          },
          calcScaleFactor = (debugMode = false) => {
            const _maxWidth = maxWidthOfTextChord(debugMode),
              _maxLineWidth = Math.max(
                ...Array.from({ length: this._textLines.length }, (_, i) => this.measureLine(i).width || 0)
              ),
              _scaleFactorW = _maxWidth / _maxLineWidth,
              _scaleFactorMaxW = (_maxWidth + boundaryPadding) / _maxLineWidth,
              _scaleFactorH = (fabric.BASE_TAG_RADIUS * Number(this.heightScaleFactor || 1)) / this.__lineHeights[0];
            return {
              maxWidth: _maxWidth,
              scaleFactorW: _scaleFactorW,
              scaleFactorH: _scaleFactorH,
              scaleFactorMaxW: _scaleFactorMaxW,
              scaleFactor: Math.min(_scaleFactorH || 1, _scaleFactorW || 1),
            };
          },
          calcScaleAndSetFontSize = () => {
            const calculatedScaleFactor = calcScaleFactor();
            this.setFontSize(!fabric.DESIGNER_MODE ? this.fontSize * calculatedScaleFactor.scaleFactor : this.fontSize);

            return this.fontSize;
          };

        calcScaleAndSetFontSize();
        this.boundingRect = new fabric.Rect({
          top: this.getCenterPoint().y,
          left: this.getCenterPoint().x,
          originY: 'center',
          originX: 'center',
          width: this.measureLine(0).width,
          height: this.fontSize,
          fill: 'transparent',
          stroke: this.fill || 'black',
        });
        if (fabric.DEBUG) {
          this.group?.addWithUpdate(this.boundingRect);
          this.group?.addWithUpdate(
            new fabric.Rect({
              top: this.getCenterPoint().y,
              left: this.getCenterPoint().x,
              originY: 'center',
              originX: 'center',
              width: maxWidthOfTextChord(true),
              height: this.fontSize,
              fill: 'transparent',
              stroke: 'green',
            })
          );
        }
        calcScaleAndSetFontSize();
        return this;
      };

      // Fabric Group
      fabric.Group.prototype.addTextLine = function (
        textContent,
        {
          color,
          offsetX,
          offsetY,
          options,
          fontFamily,
          lineOptions,
          fontSize,
          minFontSize,
          maxFontSize,
          labelOptions,
          maxCharacters,
          boundaryPadding,
          heightScaleFactor,
        },
        beforeAddCallback = null
      ) {
        const FabricText = new fabric.Text(textContent, {
          offsetY: fabric.DESIGNER_MODE ? Number(offsetY) : fabric.BASE_TAG_RING_RADIUS + Number(offsetY),
          top: fabric.DESIGNER_MODE ? Number(offsetY) : fabric.BASE_TAG_RING_RADIUS + Number(offsetY),
          offsetX: fabric.DESIGNER_MODE ? Number(offsetX) : -Number(offsetX),
          left: fabric.DESIGNER_MODE ? Number(offsetX) : -Number(offsetX),
          originY: 'center',
          originX: 'center',
          fill: color,
          group: this,
          textAlign: 'center',
          boundaryPadding,
          heightScaleFactor,
          ...options,
        })
          .setCustomFontFamily(fontFamily)
          .setMaxCharacters(maxCharacters);
        if (fontSize) FabricText.fontSize = fontSize;
        FabricText.initDimensions();
        FabricText.setCoords();
        FabricText.minFontSize = minFontSize;
        FabricText.maxFontSize = maxFontSize;
        if (!fabric.DESIGNER_MODE)
          FabricText.autoScaleFonts(
            Number(offsetX) >= 0 ? Number(boundaryPadding) + Number(offsetX) : Number(boundaryPadding) - Number(offsetX)
          );
        if (beforeAddCallback) beforeAddCallback(this);
        this.addWithUpdate(FabricText);
        if (lineOptions && labelOptions) FabricText.addTextLabel(labelOptions.text, labelOptions, lineOptions);
        return FabricText;
      };
      fabric.Group.prototype.scaleToCanvas = function (canvasWidth, canvasHeight) {
        const scaleFactor = canvasWidth / canvasHeight;
        let counter = 0;
        while (this.getBoundingRect(true, true).width > canvasWidth || this.getBoundingRect(true, true).height > canvasHeight) {
          if (counter >= 3) break;
          if (this.getBoundingRect(true, true).width > canvasWidth) {
            canvasWidth = this.scaleToWidth(canvasWidth, true).width;
            canvasHeight = canvasWidth / scaleFactor;
          } else {
            canvasHeight = this.scaleToHeight(canvasHeight, true).height;
            canvasWidth = canvasHeight * scaleFactor;
          }
          counter++;
        }
        return this;
      };
    },
    // Adds title in the canvas
    addCanvasTitle(title) {
      const Title = new fabric.Text(title, {
        fontSize: 17,
        top: this.canvas.height - 30,
        fill: '#6b6b6b',
        fontWeight: 500,
        selectable: false,
        excludeFromExport: true,
        fontFamily: 'lato',
      }).setCoords();
      this.canvas.centerObjectH(Title).add(Title);
      this.canvasTitleHeight = Title.height + 30;
    },
    activateDesignerMode(activate = true) {
      this.designerMode = fabric.DESIGNER_MODE = activate;
      const _loadCurrentConfig = {
        petNameText: null,
        phoneText: null,
        designerMode: false,
      };
      if (this.designerMode === true && fabric.DESIGNER_MODE === true) {
        _loadCurrentConfig.designerMode = true;
      }
      if (this.FabricPetNameText) {
        _loadCurrentConfig.petNameText = {
          fontSize: Number(this.FabricPetNameText.fontSize),
          offsetY: Number(this.FabricPetNameText.offsetY),
          offsetX: Number(this.FabricPetNameText.offsetX),
          options: { originY: this.FabricPetNameText?.originY || 'center' },
        };
      }
      if (this.FabricPhoneText) {
        _loadCurrentConfig.phoneText = {
          fontSize: Number(this.FabricPhoneText.fontSize),
          offsetY: Number(this.FabricPhoneText.offsetY),
          offsetX: Number(this.FabricPhoneText.offsetX),
        };
      }
      return _loadCurrentConfig;
    },
    reduceFontSize(lineNo) {
      const _loadCurrentConfig = this.activateDesignerMode();
      if (lineNo == 1 && this.FabricPetNameText) {
        _loadCurrentConfig.petNameText.fontSize = this.FabricPetNameText.setFontSize(_loadCurrentConfig.petNameText.fontSize - 5)?.fontSize;
      }
      if (lineNo == 2 && this.FabricPhoneText) {
        _loadCurrentConfig.phoneText.fontSize = this.FabricPhoneText.setFontSize(_loadCurrentConfig.phoneText.fontSize - 5)?.fontSize;
      }
      this.designerModeOverrideConfig = _loadCurrentConfig;
    },
    increaseFontSize(lineNo) {
      const _loadCurrentConfig = this.activateDesignerMode();
      if (lineNo == 1 && this.FabricPetNameText) {
        _loadCurrentConfig.petNameText.fontSize = this.FabricPetNameText.setFontSize(_loadCurrentConfig.petNameText.fontSize + 5)?.fontSize;
      }
      if (lineNo == 2 && this.FabricPhoneText) {
        _loadCurrentConfig.phoneText.fontSize = this.FabricPhoneText.setFontSize(_loadCurrentConfig.phoneText.fontSize + 5)?.fontSize;
      }
      this.designerModeOverrideConfig = _loadCurrentConfig;
    },
    moveUp(lineNo) {
      const _loadCurrentConfig = this.activateDesignerMode();
      if (lineNo == 1 && this.FabricPetNameText) {
        _loadCurrentConfig.petNameText.offsetY = this.FabricPetNameText.setPosition(
          _loadCurrentConfig.petNameText.offsetY - 10,
          _loadCurrentConfig.petNameText.offsetX
        )?.offsetY;
      }
      if (lineNo == 2 && this.FabricPhoneText) {
        _loadCurrentConfig.phoneText.offsetY = this.FabricPhoneText.setPosition(
          _loadCurrentConfig.phoneText.offsetY - 10,
          _loadCurrentConfig.phoneText.offsetX
        )?.offsetY;
      }
      this.designerModeOverrideConfig = _loadCurrentConfig;
    },
    moveDown(lineNo) {
      const _loadCurrentConfig = this.activateDesignerMode();
      if (lineNo == 1 && this.FabricPetNameText) {
        _loadCurrentConfig.petNameText.offsetY = this.FabricPetNameText.setPosition(
          _loadCurrentConfig.petNameText.offsetY + 10,
          _loadCurrentConfig.petNameText.offsetX
        )?.offsetY;
      }
      if (lineNo == 2 && this.FabricPhoneText) {
        _loadCurrentConfig.phoneText.offsetY = this.FabricPhoneText.setPosition(
          _loadCurrentConfig.phoneText.offsetY + 10,
          _loadCurrentConfig.phoneText.offsetX
        )?.offsetY;
      }
      this.designerModeOverrideConfig = _loadCurrentConfig;
    },
    moveLeft(lineNo) {
      const _loadCurrentConfig = this.activateDesignerMode();
      if (lineNo == 1 && this.FabricPetNameText) {
        _loadCurrentConfig.petNameText.offsetX = this.FabricPetNameText.setPosition(
          _loadCurrentConfig.petNameText.offsetY,
          _loadCurrentConfig.petNameText.offsetX - 10
        )?.offsetX;
      }
      if (lineNo == 2 && this.FabricPhoneText) {
        _loadCurrentConfig.phoneText.offsetX = this.FabricPhoneText.setPosition(
          _loadCurrentConfig.phoneText.offsetY,
          _loadCurrentConfig.phoneText.offsetX - 10
        )?.offsetX;
      }
      this.designerModeOverrideConfig = _loadCurrentConfig;
    },
    moveRight(lineNo) {
      const _loadCurrentConfig = this.activateDesignerMode();
      if (lineNo == 1 && this.FabricPetNameText) {
        _loadCurrentConfig.petNameText.offsetX = this.FabricPetNameText.setPosition(
          _loadCurrentConfig.petNameText.offsetY,
          _loadCurrentConfig.petNameText.offsetX + 10
        )?.offsetX;
      }
      if (lineNo == 2 && this.FabricPhoneText) {
        _loadCurrentConfig.phoneText.offsetX = this.FabricPhoneText.setPosition(
          _loadCurrentConfig.phoneText.offsetY,
          _loadCurrentConfig.phoneText.offsetX + 10
        )?.offsetX;
      }
      this.designerModeOverrideConfig = _loadCurrentConfig;
    },
    clearCanvas() {
      const CanvasJSON = this.canvas?.toJSON();
      this.canvas?.clear();
      this.canvas?.loadFromJSON(CanvasJSON);
    },
    renderCanvas(forExport = false) {
      forExport ? this.exportCanvas?.renderAll()?.calcOffset() : this.canvas?.renderAll();
    },
    exportCanvasLaserFront() {
      if (!this.exportCanvas) this.loadExportCanvas();
      return this.exportCanvas?.toDataURL('png');
    },
    exportCanvasLaserFrontSvg() {
      if (!this.exportCanvas) this.loadExportCanvas();
      const rawSVG = this.exportCanvas?.toSVG();
      return DOMPurify.sanitize(rawSVG, { USE_PROFILES: { svg: true } });
    },
    // This would export front of the tag for display tag under pet tags (PO Account) and basket (Order App)
    exportCanvasPreview() {
      const previewCanvas = new fabric.Canvas().setHeight(500).setWidth(500),
        canvasJSON = this.canvas.toJSON();
      canvasJSON.background = 'transparent';
      previewCanvas.loadFromJSON(canvasJSON, previewCanvas.renderAll.bind(previewCanvas), function (o, object) {
        previewCanvas.centerObject(object.scaleToWidth(500).scaleToHeight(500));
      });
      const previewCanvasPNG = previewCanvas?.toDataURL('png');
      previewCanvas.clear().dispose().remove();
      return previewCanvasPNG;
    },
    sanitizeDesignConfig(data) {
      if (Array.isArray(data)) {
        return data.map((item) => this.sanitizeDesignConfig(item));
      }

      if (data !== null && typeof data === 'object') {
        const cleanObj = {};
        for (const [key, value] of Object.entries(data)) {
          cleanObj[key] = this.sanitizeDesignConfig(value);
        }
        return cleanObj;
      }

      if (typeof data === 'string') {
        return DOMPurify.sanitize(data, {
          USE_PROFILES: { html: true },
          ALLOWED_TAGS: [],
        });
      }

      return data;
    },
    debounce(func, timeout = 300) {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          func.apply(this, args);
        }, timeout);
      };
    },
    onWindowResize() {
      this.windowWidth = window.innerWidth;
      this.debounce(() => {
        this.loadCanvas();
      }, 500);
    },
  },
};
</script>
